import * as React from 'react'
import { IMemberSearchObject } from '../../../types'
import ErrorMessage from '../../ui/ErrorMessage'
import PreloaderCircle from '../../ui/PreloaderCircle'
import TeamMemberViewHeader from './TeamMemberViewHeader'
import TeamMemberPaneHeader from '../TeamMemberPaneHeader'
import ImageView from 'src/components/ui/ImageView'
import { TEAM_MEMBER_PANE_TAB } from '../../../state/constants/index'
import TeamMemberPermissionPane from '../../../containers/team-members/TeamMemberPermissionPane'
import UIMessages from '../../../utils/ui-messages'
import toast, { Toaster } from 'react-hot-toast'
import { Roles } from 'src/utils/authorization'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import FormLabel from 'src/components/ui/form/FormLabel'

export interface ITeamMemberViewPaneProps {
  organizationName: string
  teamMember?: IMemberSearchObject
  isFetching: boolean
  error: string
  fetchTeamMember: () => void
  onArchiveClick: (teamMember: IMemberSearchObject) => any
  onResetClick: (teamMember: IMemberSearchObject) => any
  onSendInviteClick: (teamMember: IMemberSearchObject) => any
}

export interface ITeamMemberViewPaneParams {
  id: string
}

interface IMainInfoProps {
  avatarUrl: string
  name: string
  roles: string[]
  id: string
}

interface IDetailsInfoProps {
  countryCode: string
  email: string
  firstName: string
  lastName: string
  organizationName: string
  phoneNumber: string
  roles: string[]
  username: string
}

const MainInfo = ({ avatarUrl, name, roles, id }: IMainInfoProps) => {
  const userRoles = roles.filter(role => role)
  return (
    <div className='column-gap-16 flex items-center'>
      <div className='pb1'>
        <ImageView name={name} entityId={id} isRounded={true} />
      </div>
      <div>
        <p className='fs-20 font-grey3 m0'>{name}</p>
        <div className='h5'>{userRoles.join(', ')}</div>
      </div>
    </div>
  )
}

const DetailsInfo = ({
  countryCode,
  email,
  firstName,
  lastName,
  organizationName,
  phoneNumber,
  roles,
  username
}: IDetailsInfoProps) => {
  const {
    roles: rolesLabel,
    form: { email: emailLabel, organization, phoneNumber: phoneNumberLabel, username: usernameLabel }
  } = UIMessages.teamMember
  const formattedPhoneNumber = phoneNumber !== '' ? `+${countryCode || ''} ${phoneNumber}` : ''
  const personalInfoMapping = [
    { label: organization, value: organizationName },
    { label: usernameLabel, value: username },
    { label: emailLabel, value: email },
    { label: phoneNumberLabel, value: formattedPhoneNumber },
    { label: rolesLabel, value: roles.join(', ') }
  ]

  return (
    <>
      {personalInfoMapping.map(({ label, value }) => (
        <div className='mbsc-col-12 mbsc-col-md-6 mbsc-col-lg-4 mbsc-padding' key={label}>
          <FormLabel>{label}</FormLabel>
          <div className='h5 mt1'>{value}</div>
        </div>
      ))}
    </>
  )
}

interface ITeamMemberViewPaneState {
  isSaving: boolean
  tab: string
}

class TeamMemberViewPane extends React.Component<ITeamMemberViewPaneProps, ITeamMemberViewPaneState> {
  constructor(props: ITeamMemberViewPaneProps) {
    super(props)
    this.state = {
      isSaving: false,
      tab: TEAM_MEMBER_PANE_TAB.PERSONAL_INFO
    }
  }
  public componentDidMount(): void {
    const { fetchTeamMember } = this.props
    fetchTeamMember()
  }

  public render() {
    const { organizationName, teamMember, isFetching, error } = this.props
    const { tab } = this.state
    const style: any = {
      pointerEvents: this.state.isSaving ? 'none' : 'auto',
      opacity: this.state.isSaving ? 0.4 : 1.0
    }

    const getTeamMemberRoles = () => {
      const roles: string[] = []
      const getRoles = teamMember
        ? teamMember.roles.forEach(role => {
            if (role === Roles.ORGANIZATION_DISPATCHER) {
              roles.push('Dispatcher')
            } else if (role === Roles.ORGANIZATION_ADMIN) {
              roles.push('Admin')
            }
          })
        : null
      return roles
    }

    return (
      <div style={style}>
        {isFetching && (
          <div className='flex justify-center pt3'>
            <PreloaderCircle loading={true} />
          </div>
        )}
        <Toaster position='top-right' reverseOrder={false} />
        {<ErrorMessage visible={!!error}>{error}</ErrorMessage>}

        {!isFetching && teamMember && (
          <div>
            <TeamMemberViewHeader
              isArchived={teamMember.isArchived}
              isFetching={isFetching}
              name={teamMember.name}
              status={teamMember.status}
              teamMember={teamMember}
              onMoveToArchive={this.onMoveToArchive}
              onResetPassword={this.onResetPassword}
              onSendInvitation={this.onSendInvitation}
            />
            <TeamMemberPaneHeader
              currentTab={tab}
              changeTab={t => {
                this.setState({ tab: t })
              }}
            />
            {tab === TEAM_MEMBER_PANE_TAB.PERSONAL_INFO && (
              <section className='tm-personal-info-details-container'>
                <FloatingForm>
                  <div className='mbsc-grid' style={{ padding: '0' }}>
                    <div className='mbsc-row'>
                      <div className='mbsc-col-12 mbsc-padding'>
                        <MainInfo
                          avatarUrl='https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPAD7cIOpVaM6tNyFR1LY52GN6ZTV34SDIV9S4iSJzpN-FK5SXvw'
                          id={this.props.teamMember ? `${this.props.teamMember.userId}` : ''}
                          name={teamMember.name}
                          roles={getTeamMemberRoles()}
                        />
                      </div>
                      <DetailsInfo
                        countryCode={teamMember.countryCode}
                        email={teamMember.email}
                        firstName={teamMember.firstName}
                        lastName={teamMember.lastName}
                        organizationName={organizationName}
                        phoneNumber={teamMember.phone}
                        roles={getTeamMemberRoles()}
                        username={teamMember.username}
                      />
                    </div>
                  </div>
                </FloatingForm>
              </section>
            )}
            {tab === TEAM_MEMBER_PANE_TAB.PERMISSION && <TeamMemberPermissionPane id={teamMember.id} />}
          </div>
        )}
      </div>
    )
  }

  private onSendInvitation = () => {
    this.setState(
      {
        isSaving: true
      },
      () => {
        if (!this.props.teamMember) {
          return
        }
        this.props
          .onSendInviteClick(this.props.teamMember)
          .then(() => {
            toast.success(UIMessages.teamMember.invitationMessage)
            this.props.fetchTeamMember()
          })
          .catch((error: unknown) => {
            toast.error('Failed to send invitation.')
            console.error(error)
          })
          .finally(() => {
            this.setState({ isSaving: false })
          })
      }
    )
  }

  private onResetPassword = () => {
    this.setState(
      {
        isSaving: true
      },
      () => {
        if (!this.props.teamMember) {
          return
        }
        this.props
          .onResetClick(this.props.teamMember)
          .then(() => {
            toast.success(UIMessages.teamMember.resetPasswordMessage)
            this.props.fetchTeamMember()
          })
          .catch((error: unknown) => {
            toast.error('Failed to reset password.')
            console.error(error)
          })
          .finally(() => {
            this.setState({ isSaving: false })
          })
      }
    )
  }

  private onMoveToArchive = () => {
    this.setState(
      {
        isSaving: true
      },
      () => {
        if (!this.props.teamMember) {
          return
        }
        this.props
          .onArchiveClick(this.props.teamMember)
          .then(() => {
            toast.success(UIMessages.teamMember.archivingUnarchivingMessage)
            this.props.fetchTeamMember()
          })
          .catch((error: unknown) => {
            toast.error('An error occurred.')
            console.error(error)
          })
          .finally(() => {
            this.setState({ isSaving: false })
          })
      }
    )
  }
}

export default TeamMemberViewPane



// WEBPACK FOOTER //
// ./src/components/team-members/view/TeamMemberViewPane.tsx